// In-memory network log for internal debugging. Capture is near-free: entries hold
// lazy JSON thunks (closures over proto messages already in memory) instead of eagerly
// serialized payloads, and the whole log is a bounded ring buffer that lives only for
// the lifetime of the tab. Module-level (not React context) so per-request writes never
// re-render anything except a mounted log panel; consume via `useNetworkLogState`.

import { getPersistedAdminOptionsWithDefaults } from "@/utils/admin-options-manager";
import { parseEnvironmentIdFromUrl } from "@/utils/connect-query/environment-id";
import { LocalStorageKey } from "@/utils/local-storage-utils";

export type NetworkLogStatus = "pending" | "success" | "error" | "cancelled";

export type NetworkLogMethodKind =
  | "unary"
  | "server_streaming"
  | "client_streaming"
  | "bidi_streaming";

export interface NetworkLogError {
  name: string;
  message: string;
  code?: number;
  codeName?: string;
}

export interface NetworkLogEntry {
  id: number;
  serviceName: string;
  methodName: string;
  methodKind: NetworkLogMethodKind;
  route: string;
  environmentId: string | null;
  startedAt: string;
  durationMs: number | null;
  status: NetworkLogStatus;
  error?: NetworkLogError;
  /** Payload capture was skipped because the method matched the sensitive skip list. */
  redacted: boolean;
  getRequestJson?: () => unknown;
  getResponseJson?: () => unknown;
  /** Total messages seen on a streaming response (may exceed the number captured). */
  responseMessageCount?: number;
  /**
   * Re-issues this call through the transport that made it (the fresh attempt is
   * captured as its own entry). Absent for redacted methods; withdrawn at settlement
   * for streams whose request messages were not all captured.
   */
  replay?: () => Promise<void>;
}

export interface NetworkLogState {
  entries: readonly NetworkLogEntry[];
  panelOpen: boolean;
  captureEnabled: boolean;
}

export interface BeginNetworkLogEntryInit {
  serviceName: string;
  methodName: string;
  methodKind: NetworkLogMethodKind;
  redacted: boolean;
  getRequestJson?: () => unknown;
  replay?: () => Promise<void>;
}

export interface ProgressNetworkLogEntryPatch {
  getResponseJson?: () => unknown;
  responseMessageCount?: number;
}

export interface CompleteNetworkLogEntryPatch {
  status: Exclude<NetworkLogStatus, "pending">;
  error?: NetworkLogError;
  getResponseJson?: () => unknown;
  responseMessageCount?: number;
  /** Overrides the store-measured duration (used by synthetic test entries). */
  durationMs?: number;
  /**
   * Include (as undefined) only to withdraw the entry's replay affordance — e.g.
   * when a stream's request capture turned out to be truncated, so a replay could
   * never faithfully re-issue the call.
   */
  replay?: undefined;
}

export const NETWORK_LOG_MAX_ENTRIES = 300;

const EMPTY_STATE: NetworkLogState = {
  entries: [],
  panelOpen: false,
  captureEnabled: false,
};

// Payloads bigger than this are dropped when compacting a closed session; a whole
// retained session is then at most ~MAX_ENTRIES × this bound.
const COMPACTED_PAYLOAD_MAX_CHARS = 2_000_000;

function readPersistedPanelOpen(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return localStorage.getItem(LocalStorageKey.NETWORK_LOG_PANEL_OPEN) === "true";
  } catch {
    return false;
  }
}

function persistPanelOpen(panelOpen: boolean): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(LocalStorageKey.NETWORK_LOG_PANEL_OPEN, String(panelOpen));
  } catch {
    // Best effort — a full/blocked localStorage only costs persistence.
  }
}

// The drawer's open state survives reloads so a recording session can span a
// refresh. Capture starts as soon as this module loads (i.e. before
// NetworkLogBootstrap mounts and re-derives it) so the page load's own
// requests — which fire once and then sit in the query cache — are captured
// too. The flag can only have been persisted from the employee-only drawer;
// the bootstrap still turns capture off once it knows the viewer isn't
// eligible. The "Disable network log" admin option is honored here as well —
// requests can fire before the bootstrap's effect runs, so the hard-disable
// must not be bypassable by a stale persisted-open flag.
const initialPanelOpen =
  readPersistedPanelOpen() && !getPersistedAdminOptionsWithDefaults().disableNetworkLog;

let state: NetworkLogState = initialPanelOpen
  ? { ...EMPTY_STATE, panelOpen: true, captureEnabled: true }
  : EMPTY_STATE;
let nextEntryId = 1;
const entryStartTimesMs = new Map<number, number>();
const listeners = new Set<() => void>();

function emit(next: NetworkLogState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

export function subscribeToNetworkLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getNetworkLogState(): NetworkLogState {
  return state;
}

// Stable reference for useSyncExternalStore's server snapshot.
export function getNetworkLogServerState(): NetworkLogState {
  return EMPTY_STATE;
}

export function isNetworkLogCaptureEnabled(): boolean {
  return state.captureEnabled;
}

export function setNetworkLogCaptureEnabled(captureEnabled: boolean): void {
  if (state.captureEnabled === captureEnabled) {
    return;
  }
  emit({ ...state, captureEnabled });
}

export function setNetworkLogPanelOpen(panelOpen: boolean): void {
  if (state.panelOpen === panelOpen) {
    return;
  }
  persistPanelOpen(panelOpen);
  emit({ ...state, panelOpen });
  if (!panelOpen && typeof window !== "undefined") {
    // Off the critical path: release proto references once the session is closed.
    window.setTimeout(compactNetworkLogPayloads, 0);
  }
}

export function clearNetworkLog(): void {
  entryStartTimesMs.clear();
  emit({ ...state, entries: [] });
}

export function beginNetworkLogEntry(init: BeginNetworkLogEntryInit): number {
  const id = nextEntryId;
  nextEntryId += 1;
  entryStartTimesMs.set(id, Date.now());

  const href = typeof window !== "undefined" ? window.location : null;
  const entry: NetworkLogEntry = {
    ...init,
    id,
    route: href ? `${href.pathname}${href.search}${href.hash}` : "",
    environmentId: href ? parseEnvironmentIdFromUrl(href.href) : null,
    startedAt: new Date().toISOString(),
    durationMs: null,
    status: "pending",
  };

  const entries = [...state.entries, entry];
  if (entries.length > NETWORK_LOG_MAX_ENTRIES) {
    const evicted = entries.splice(0, entries.length - NETWORK_LOG_MAX_ENTRIES);
    evicted.forEach((evictedEntry) => entryStartTimesMs.delete(evictedEntry.id));
  }
  emit({ ...state, entries });
  return id;
}

/**
 * Update a still-pending entry with in-flight streaming capture (message count and a
 * response thunk) without settling it, so long-lived streams show their messages in
 * the drawer as they arrive. No-op once the entry has settled, been evicted, or been
 * cleared — settlement remains the authoritative final patch.
 */
export function progressNetworkLogEntry(id: number, patch: ProgressNetworkLogEntryPatch): void {
  const index = state.entries.findIndex((entry) => entry.id === id);
  if (index === -1 || state.entries[index].status !== "pending") {
    return;
  }
  const entries = [...state.entries];
  entries[index] = { ...entries[index], ...patch };
  emit({ ...state, entries });
}

export function completeNetworkLogEntry(id: number, patch: CompleteNetworkLogEntryPatch): void {
  const startedAtMs = entryStartTimesMs.get(id);
  entryStartTimesMs.delete(id);

  const index = state.entries.findIndex((entry) => entry.id === id);
  // The entry may have been evicted from the ring buffer or cleared while in flight.
  if (index === -1) {
    return;
  }
  // First settlement wins: the interceptor's abort listener and its stream generator
  // can both try to settle the same entry.
  if (state.entries[index].status !== "pending") {
    return;
  }

  const { durationMs, ...rest } = patch;
  const entries = [...state.entries];
  entries[index] = {
    ...entries[index],
    ...rest,
    durationMs: durationMs ?? (startedAtMs != null ? Date.now() - startedAtMs : null),
  };
  emit({ ...state, entries });
}

export interface NetworkLogExportBundle {
  exportedAt: string;
  source: {
    href?: string;
    userAgent?: string;
  };
  entryCount: number;
  entries: Array<
    Omit<NetworkLogEntry, "getRequestJson" | "getResponseJson" | "replay"> & {
      request?: unknown;
      response?: unknown;
    }
  >;
}

export function makeNetworkLogExportBundle(): NetworkLogExportBundle {
  return {
    exportedAt: new Date().toISOString(),
    source: {
      href: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    },
    entryCount: state.entries.length,
    entries: state.entries.map((entry) => {
      const { getRequestJson, getResponseJson, replay: _replay, ...rest } = entry;
      return {
        ...rest,
        request: getRequestJson?.(),
        response: getResponseJson?.(),
      };
    }),
  };
}

export function getNetworkLogExportFilename(): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `chalk-network-log-${timestamp}.json`;
}

/**
 * Cheap "would the serialized JSON exceed `budgetChars`?" probe that walks the value
 * with a character budget and bails out early, instead of materializing a full
 * (possibly multi-MB) JSON string just to measure it. Approximate on purpose.
 */
export function jsonCharSizeExceeds(value: unknown, budgetChars: number): boolean {
  let remaining = budgetChars;
  const stack: unknown[] = [value];
  while (stack.length > 0) {
    if (remaining <= 0) {
      return true;
    }
    const current = stack.pop();
    if (current == null) {
      remaining -= 4;
    } else if (typeof current === "string") {
      remaining -= current.length + 2;
    } else if (Array.isArray(current)) {
      remaining -= 2;
      for (const item of current) {
        stack.push(item);
      }
    } else if (typeof current === "object") {
      remaining -= 2;
      for (const [key, item] of Object.entries(current)) {
        remaining -= key.length + 4;
        stack.push(item);
      }
    } else {
      remaining -= 8;
    }
  }
  return remaining <= 0;
}

function materializePayloadThunk(
  getJson: (() => unknown) | undefined,
): (() => unknown) | undefined {
  if (getJson == null) {
    return undefined;
  }
  let json: unknown;
  try {
    json = getJson();
  } catch (error) {
    json = { captureError: error instanceof Error ? error.message : String(error) };
  }
  if (jsonCharSizeExceeds(json, COMPACTED_PAYLOAD_MAX_CHARS)) {
    json = {
      networkLogRetentionNote: `payload dropped: exceeded the ~${COMPACTED_PAYLOAD_MAX_CHARS.toLocaleString()}-char retention cap after the drawer closed`,
    };
  }
  return () => json;
}

/**
 * Replace lazy payload thunks (closures pinning live proto messages) with plain JSON
 * so a closed session doesn't retain proto object graphs for the rest of the tab's
 * lifetime. Runs when the drawer closes; entries still pending then keep their thunks
 * until they settle (rare, small, and released on eviction/clear).
 */
export function compactNetworkLogPayloads(): void {
  if (state.entries.length === 0) {
    return;
  }
  const entries = state.entries.map((entry) =>
    entry.status === "pending"
      ? entry
      : {
          ...entry,
          getRequestJson: materializePayloadThunk(entry.getRequestJson),
          getResponseJson: materializePayloadThunk(entry.getResponseJson),
          // Replay closures pin the live request protos too — release them with
          // the payload thunks; the entry just loses its replay affordance.
          replay: undefined,
        },
  );
  emit({ ...state, entries });
}
